// Collage engine — per-project skins (collage_engine_spec v0 §Print pass).
// A skin is palette + print-pass defaults + verb/edge weighting: the
// "per-project skins" tier of the three-tier mapping. Values are the v1
// presets from the spec's §Skin presets v1 (Order 152) — the spec page is
// canonical for these values; tune there, re-serialize here.
//
// WHY THIS LIVES IN ANIM-SCHEMA (Order 154): both hosts — the Compositor
// composer and the Marionette scene renderer — consume the same recipe grammar,
// and a recipe names a skin by id. Preset VALUES and the pure resolution
// helpers are host-neutral data, and duplicating them per host is a drift bug
// waiting to happen (Order 152 just re-tuned every value). Each host still
// owns its registry SURFACE (lookup/extension policy stays per-host, per the
// collage-recipe contract); this module is the one home for the shared values
// and the pure functions. Dependency-free, like everything here.
//
// Ink names are the recipe's color vocabulary: a recipe layer says
// { kind: 'solid', ink: 'mustard' } and the composer resolves it through the
// active skin. Raw hex is also accepted anywhere an ink name is (escape hatch).
//
// ROLES — the cross-skin vocabulary. Palettes are per-project by design, so a
// recipe that names a skin's inks can't survive a skin swap (the 3×3 matrix:
// any template recipe × any skin). Each skin therefore maps five canonical
// roles onto its own inks; recipes that want to be skin-portable (the shipped
// templates are) reference roles instead of ink names. `paper` always resolves
// to paper_hex.

import type {
  CollagePrintPass, CollageEdgeFamily, CollageMotionVerb,
  CollageBoil, CollageFrameCadence,
} from './collage';

// Canonical cross-skin ink roles. `paper` resolves to the skin's paper_hex;
// the other four map to named inks per skin.
export const INK_ROLES = ['paper', 'primary', 'secondary', 'tertiary', 'dark'] as const;
export type InkRole = (typeof INK_ROLES)[number];

// A skin's vector type slot (reference set 3 → Order 156/158): display carries
// the loud register, grotesk the editorial one. `family` is the primary family
// name (a Google-Fonts-loadable family, so both hosts can fetch it); `stack`
// is the full CSS stack a renderer paints with; `weights` names the three
// weights the stacked-weights treatment composes with (families without a
// range repeat one weight — the renderer never invents an unavailable weight).
export interface SkinFont {
  family: string;
  stack: string;
  weights: { light: number; regular: number; heavy: number };
}

export interface CollageSkin {
  id: string;
  name: string;
  // The paper base — the collage ground's default fill.
  paper_hex: string;
  // Named inks. Every skin must carry at least 3.
  inks: Record<string, string>;
  // The ink name text slots (title / subtitle) paint with.
  text_ink: string;
  // Role → ink-name map (paper is implicit). Every role must name an existing
  // ink — validated at module load below.
  roles: Record<Exclude<InkRole, 'paper'>, string>;
  // Type slots (spec §Vocabulary additions from reference set 3, "Skin type
  // slots"). Display type is a live layer, never a baked image.
  fonts: { display: SkinFont; grotesk: SkinFont };
  // Print-pass defaults (recipe.print overrides field-by-field; seed is stamped
  // by the composer from the recipe seed, so it is not part of the skin).
  print: Omit<CollagePrintPass, 'seed'>;
  // Order 158.4 — scene-treatment defaults (recipe fields override). Absent =
  // off: no stepped clock (wobble keeps its legacy 3-frame step), no boil.
  frame_cadence?: CollageFrameCadence;
  boil?: CollageBoil | null;
  // Seeded-pick weights for a layer that declares no motion / no edge. Weights
  // are relative (they need not sum to 1); zero/absent = never picked.
  motion_weights: Partial<Record<CollageMotionVerb | 'none', number>>;
  edge_weights: Partial<Record<CollageEdgeFamily, number>>;
}

export const COLLAGE_SKINS: Record<string, CollageSkin> = {
  // Loud and jittery: heaviest misregistration, big halftone, wobble-forward.
  'pop-riso': {
    id: 'pop-riso',
    name: 'Pop riso',
    paper_hex: '#F3EFE4',
    inks: {
      pink: '#F03FA0',
      green: '#1D8A4E',
      orange: '#F59B0C',
      blue: '#2E49C8',
      red: '#E8401C',
      ink: '#1B1720',
    },
    text_ink: 'ink',
    roles: { primary: 'pink', secondary: 'green', tertiary: 'orange', dark: 'ink' },
    // Heavy rounded display, cropped and bleeding (Cooper-family energy).
    fonts: {
      display: {
        family: 'Bowlby One',
        stack: "'Bowlby One', 'Arial Rounded MT Bold', 'Arial Black', sans-serif",
        weights: { light: 400, regular: 400, heavy: 400 },
      },
      grotesk: {
        family: 'Archivo',
        stack: "'Archivo', 'Helvetica Neue', Helvetica, sans-serif",
        weights: { light: 300, regular: 500, heavy: 900 },
      },
    },
    print: {
      grain: 0.35,
      halftone: { dot_px: 7, opacity: 0.8 },
      misregister_px: 3,
    },
    motion_weights: { drift: 3, wobble: 3, breathe: 1, reveal: 1, slide: 2, pulse: 1, twinkle: 1, none: 2 },
    edge_weights: { torn: 4, cut: 4, punched: 2 },
  },

  // Bedtime calm: slow drift and breathe, twinkle-heavy for night skies, soft
  // torn edges, gentlest print pass, no halftone.
  'nk-dusk': {
    id: 'nk-dusk',
    name: 'NK dusk',
    paper_hex: '#ECE6DA',
    inks: {
      indigo: '#2B2D5E',
      plum: '#6C3FA0',
      rose: '#D8798F',
      gold: '#E8A33D',
      teal: '#12474A',
      ink: '#14121F',
    },
    text_ink: 'ink',
    roles: { primary: 'indigo', secondary: 'gold', tertiary: 'teal', dark: 'ink' },
    // Soft rounded display, gentle weight — bedtime register.
    fonts: {
      display: {
        family: 'Baloo 2',
        stack: "'Baloo 2', 'Arial Rounded MT Bold', sans-serif",
        weights: { light: 400, regular: 500, heavy: 800 },
      },
      grotesk: {
        family: 'Nunito Sans',
        stack: "'Nunito Sans', 'Avenir Next', 'Helvetica Neue', sans-serif",
        weights: { light: 300, regular: 400, heavy: 800 },
      },
    },
    print: {
      grain: 0.25,
      halftone: null,
      misregister_px: 1,
    },
    // NK presentation tuning (Order 158.5): the house stepped clock at 12,
    // boil at a bedtime whisper (2 seeds, gentle amplitude — the torn edges
    // breathe rather than jitter). Twinkle-heavy weights preserved: the night
    // sky stays the skin's native motion.
    frame_cadence: 12,
    boil: { seeds: 2, amount: 0.15 },
    motion_weights: { drift: 4, wobble: 1, breathe: 3, reveal: 1, slide: 1, pulse: 1, twinkle: 4, none: 3 },
    edge_weights: { torn: 5, cut: 2, punched: 1 },
  },

  // Composed and editorial: reveal-forward for card entrances, cut/punched
  // geometry per the banded print template, navy text.
  'explainer-print': {
    id: 'explainer-print',
    name: 'Explainer print',
    paper_hex: '#F1EDE2',
    inks: {
      mustard: '#E8A31C',
      slate: '#3E6E8E',
      coral: '#E2714B',
      navy: '#1B2A4A',
      stone: '#8C8471',
      ink: '#22201C',
    },
    text_ink: 'navy',
    roles: { primary: 'slate', secondary: 'mustard', tertiary: 'coral', dark: 'ink' },
    // Light grotesk + black grotesk stacked (Neue Haas family, the Habitat
    // move) — one family carries both slots on purpose.
    fonts: {
      display: {
        family: 'Archivo',
        stack: "'Archivo', 'Helvetica Neue', Helvetica, sans-serif",
        weights: { light: 300, regular: 600, heavy: 900 },
      },
      grotesk: {
        family: 'Archivo',
        stack: "'Archivo', 'Helvetica Neue', Helvetica, sans-serif",
        weights: { light: 300, regular: 500, heavy: 900 },
      },
    },
    print: {
      grain: 0.2,
      halftone: { dot_px: 6, opacity: 0.6 },
      misregister_px: 2,
      // Film dust rides the house print pass (Order 158.4): sparse dark specks
      // + the odd hair, re-randomized on the cadence clock.
      dust: { density: 0.5, opacity: 0.45 },
    },
    // The expository HOUSE SKIN (Order 158.4, seam guarantee 2026-07-17):
    // stepped "on twos" clock and edge boil on by default — the stop-motion
    // stutter and the wiggling hand-drawn line are the house read, not opt-ins.
    frame_cadence: 12,
    boil: { seeds: 3, amount: 0.35 },
    motion_weights: { drift: 3, wobble: 2, breathe: 2, reveal: 3, slide: 2, pulse: 1, twinkle: 0, none: 3 },
    edge_weights: { torn: 2, cut: 4, punched: 4 },
  },
};

export function getCollageSkin(id: string): CollageSkin {
  const skin = COLLAGE_SKINS[id];
  if (!skin) {
    throw new Error(
      `unknown collage skin "${id}" — known: ${Object.keys(COLLAGE_SKINS).join(', ')}`,
    );
  }
  return skin;
}

const HEX_RE = /^#[0-9a-f]{3,8}$/i;

// Resolve an ink reference (a role, a skin ink name, or a raw hex) to hex.
// Throws on an unknown name — a typo'd ink must not silently render as black.
export function resolveInk(skin: CollageSkin, ink: string): string {
  if (HEX_RE.test(ink)) return ink;
  if (ink === 'paper') return skin.paper_hex;
  const roleTarget = (skin.roles as Record<string, string | undefined>)[ink];
  const hex = skin.inks[roleTarget ?? ink];
  if (!hex) {
    throw new Error(
      `unknown ink "${ink}" in skin "${skin.id}" — known: ${[...INK_ROLES, ...Object.keys(skin.inks)].join(', ')} (or raw #hex)`,
    );
  }
  return hex;
}

// Every skin's roles and text_ink must resolve — fail at load, not mid-compose.
for (const skin of Object.values(COLLAGE_SKINS)) {
  resolveInk(skin, skin.text_ink);
  for (const target of Object.values(skin.roles)) resolveInk(skin, target);
}

// Seeded weighted pick over a weight table. Deterministic for a given rng
// stream; used for unspecified motion/edge on a recipe layer.
export function weightedPick<K extends string>(
  weights: Partial<Record<K, number>>, rng: () => number,
): K {
  const entries = Object.entries(weights).filter(([, w]) => (w as number) > 0) as [K, number][];
  if (entries.length === 0) throw new Error('weightedPick: empty weight table');
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let roll = rng() * total;
  for (const [key, w] of entries) {
    roll -= w;
    if (roll <= 0) return key;
  }
  return entries[entries.length - 1][0];
}
